/**
 * React's view of the socket, plus the one piece of purely local state: which
 * miner is *yours*.
 */

import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react'

import type { ObserverState } from '../api/socket'
import { useObserverInstance } from './observer-context'

/** The whole live state. */
export function useObserver(): ObserverState {
  const observer = useObserverInstance()
  return useSyncExternalStore(observer.subscribe, observer.getSnapshot, observer.getSnapshot)
}

/** Subscribe to a chain and window; re-subscribes when either changes. */
export function useWatch(chain: string | null, windowName: ObserverState['window']): void {
  const observer = useObserverInstance()
  useEffect(() => {
    if (chain) observer.watch(chain, windowName)
  }, [observer, chain, windowName])
}

const PINNED_KEY = 'blackbeard.pinned-miners'

/**
 * The miners this browser calls its own.
 *
 * There are no accounts here and there is nothing to log into: a reward
 * preimage is public, so "this one is mine" is a claim the site has no way to
 * verify and no reason to. It lives in `localStorage`, which means it is
 * per-browser, survives a refresh, and never reaches the server. That is the
 * whole feature — and it is why nothing on the site is gated behind it.
 */
export function usePinnedMiners(): {
  pinned: string[]
  isPinned: (miner: string) => boolean
  toggle: (miner: string) => void
} {
  const pinned = useSyncExternalStore(subscribePinned, readPinned, () => EMPTY)

  const isPinned = useCallback((miner: string) => pinned.includes(miner.toLowerCase()), [pinned])

  const toggle = useCallback((miner: string) => {
    const id = miner.toLowerCase()
    const current = readPinned()
    const next = current.includes(id) ? current.filter((m) => m !== id) : [...current, id]
    writePinned(next)
  }, [])

  return useMemo(() => ({ pinned, isPinned, toggle }), [pinned, isPinned, toggle])
}

const EMPTY: string[] = []
let cache: string[] = EMPTY
let cacheRaw: string | null = null
const pinnedListeners = new Set<() => void>()

function subscribePinned(listener: () => void): () => void {
  pinnedListeners.add(listener)
  // `storage` fires in *other* tabs, so pinning a miner in one tab updates the
  // highlight in the others without either of them reloading.
  const onStorage = (event: StorageEvent) => {
    if (event.key === PINNED_KEY) listener()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    pinnedListeners.delete(listener)
    window.removeEventListener('storage', onStorage)
  }
}

/**
 * Read the pinned list, memoised on the raw string.
 *
 * `useSyncExternalStore` demands a stable reference between changes; parsing
 * fresh JSON on every call returns a new array each time and React throws.
 */
function readPinned(): string[] {
  let raw: string | null = null
  try {
    raw = window.localStorage.getItem(PINNED_KEY)
  } catch {
    // Private windows and "block site data" both throw rather than return null.
    return EMPTY
  }
  if (raw === cacheRaw) return cache
  cacheRaw = raw
  try {
    const parsed: unknown = raw ? JSON.parse(raw) : []
    cache = Array.isArray(parsed) ? parsed.filter((m): m is string => typeof m === 'string') : EMPTY
  } catch {
    cache = EMPTY
  }
  return cache
}

function writePinned(next: string[]): void {
  try {
    window.localStorage.setItem(PINNED_KEY, JSON.stringify(next))
  } catch {
    // Storage unavailable: the pin lasts for this render and no longer. Better
    // than refusing the click.
  }
  cacheRaw = null
  for (const listener of pinnedListeners) listener()
}
